import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Modal, Form, Row, Col, DatePicker, Button, Table, Input, notification, Card, Divider, Spin, Alert } from 'antd';
import { EyeOutlined, SendOutlined, ArrowLeftOutlined, LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import ManagerSelect from '../../ManagerSelect';
import useClientInformForm from '../../../hooks/useClientInformForm';
import useClientInformRecipients from '../../../hooks/useClientInformRecipients';
import {
  clientInformApi,
  getBuildingUnitId,
  formatDateRangeForApi,
  formatDatesForApi,
  groupRowsBySignature,
} from '../../../api/clientInformApi';
import { RecipientsCell, RecipientsSummary } from './RecipientsDisplay';
import MultiDatePicker from './MultiDatePicker';
import { getCurrentUserEmployeeId } from '../managerNameOverride';

const { RangePicker } = DatePicker;

const REQUIRED_FIELDS = ['partner_id', 'rrule'];

/**
 * Modal for informing clients about one-time cleaning day rescheduling with compensation date
 * Přesun úklidového dne (rescheduling)
 */
const CleaningDayReschedulingModal = ({ visible, onClose, selectedUnits }) => {
  const { t } = useTranslation();
  const defaultManagerEmployeeId = getCurrentUserEmployeeId();
  
  const {
    form,
    isSubmitting,
    showConfirmation,
    setIsSubmitting,
    setShowConfirmation,
    applyBulkTimeRange,
    applyBulkCompensationDate,
    resetForm,
    goBackToForm,
  } = useClientInformForm(selectedUnits, REQUIRED_FIELDS);

  const watchedChangeWeek = Form.useWatch('change_week', form);
  const watchedCompensationDate = Form.useWatch('compensation_date', form);

  const [tableData, setTableData] = useState([]);
  const { contactsByUnitId, loading: loadingContacts } = useClientInformRecipients(selectedUnits, visible);
  const [previewGroups, setPreviewGroups] = useState([]);
  const [bulkCompensationDates, setBulkCompensationDates] = useState([]);


  const validateRequiredData = useCallback(() => {
    if (!selectedUnits?.length) {
      return { valid: false, errors: [] };
    }

    const errors = [];
    
    selectedUnits.forEach((unit, index) => {
      const missingFields = [];
      const address = `${unit?.building_unit?.street || ''} ${unit?.building_unit?.house_number || ''}`.trim() || `Jednotka ${index + 1}`;
      
      if (!unit.partner_id && !unit.partner?.id) {
        missingFields.push(t('clientInform.missingPartner'));
      }
      
      if (missingFields.length > 0) {
        errors.push({
          address,
          fields: missingFields,
        });
      }
    });

    return { valid: errors.length === 0, errors };
  }, [selectedUnits]);

  useEffect(() => {
    if (visible && selectedUnits?.length) {
      const data = selectedUnits.map((unit) => ({
        key: unit.id,
        id: unit.id,
        unit: unit,
      }));
      
      setTableData(data);
      form.setFieldValue('manager_employee_id', defaultManagerEmployeeId);
    }
  }, [visible, selectedUnits, defaultManagerEmployeeId, form]);

  useEffect(() => {
    if (!visible) {
      resetForm();
      setTableData([]);
      setPreviewGroups([]);
      setBulkCompensationDates([]);
    }
  }, [visible, resetForm]);

  const rowGroups = useMemo(() => (
    groupRowsBySignature(tableData, (row) => {
      const unit = row.unit;
      const week = formatDateRangeForApi(watchedChangeWeek?.[unit.id]);
      const dates = formatDatesForApi(watchedCompensationDate?.[unit.id]);
      return [dates ?? '', week].join('|');
    })
  ), [tableData, watchedChangeWeek, watchedCompensationDate]);

  const buildGroupLabel = useCallback((rows) => {
    const addresses = rows
      .map((row) => {
        const bu = row.unit.building_unit;
        return `${bu?.street || ''} ${bu?.house_number || ''}`.trim();
      })
      .filter(Boolean)
      .join(', ');

    const firstUnit = rows[0].unit;
    const week = formatDateRangeForApi(watchedChangeWeek?.[firstUnit.id]) || '-';
    const dates = formatDatesForApi(watchedCompensationDate?.[firstUnit.id]) || '-';

    return `${addresses || '-'} — ${week}, ${dates}`;
  }, [watchedChangeWeek, watchedCompensationDate]);

  const buildPayloadGroups = useCallback(() => {
    const values = form.getFieldsValue();

    return rowGroups.map((rows) => {
      const firstUnit = rows[0].unit;

      const payload = {
        type: 'rescheduling',
        building_units: rows.map(row => row.unit.building_unit_id || row.unit.building_unit?.id),
        partner_id: firstUnit?.partner_id || firstUnit?.partner?.id,
        manager_employee_id: values.manager_employee_id || null,
      };

      const firstUnitWeek = values.change_week?.[firstUnit?.id];
      if (firstUnitWeek) {
        payload.week = formatDateRangeForApi(firstUnitWeek);
      }

      const firstUnitDates = formatDatesForApi(values.compensation_date?.[firstUnit?.id]);
      if (firstUnitDates) {
        payload.date = firstUnitDates;
      }

      return { payload, units: rows.map(row => row.unit), label: buildGroupLabel(rows) };
    });
  }, [form, rowGroups, buildGroupLabel]);

  const fetchPreviewGroups = useCallback(async () => {
    const groups = buildPayloadGroups();
    const responses = await Promise.all(
      groups.map(group => clientInformApi.preview(group.payload))
    );

    return groups.map((group, index) => ({
      key: group.label,
      label: group.label,
      html: responses[index]?.data?.html || '',
      recipients: responses[index]?.data?.recipients || [],
      buildingsWithoutContacts: responses[index]?.data?.buildings_without_contacts || [],
    }));
  }, [buildPayloadGroups]);

  const handleValidateAndPreview = async () => {
    try {
      setIsSubmitting(true);
      
      // First validate required data from selectedUnits
      const validation = validateRequiredData();
      if (!validation.valid) {
        const errorList = validation.errors.map(err => 
          `• ${err.address}: chybí ${err.fields.join(', ')}`
        ).join('\n');
        
        notification.error({
          message: t('clientInform.missingData'),
          description: (
            <div>
              <p style={{ marginBottom: 8 }}>{t('clientInform.missingDataDescription')}</p>
              <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px', marginBottom: 0 }}>
                {errorList}
              </pre>
              <p style={{ marginTop: 8, marginBottom: 0 }}>
                {t('clientInform.missingDataNote')}
              </p>
            </div>
          ),
          duration: 10,
        });
        return;
      }
      
      // Then validate form fields
      await form.validateFields();

      const groups = await fetchPreviewGroups();
      setPreviewGroups(groups);
      setShowConfirmation(true);

    } catch (error) {
      console.error('Validation or preview failed:', error);

      if (error.errorFields) {
        notification.error({
          message: t('clientInform.validationError'),
          description: t('clientInform.fillAllRequired'),
        });
      } else {
        notification.error({
          message: t('clientInform.previewError'),
          description: error?.response?.data?.message || t('clientInform.previewFailed'),
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePreview = async () => {
    try {
      setIsSubmitting(true);
      await form.validateFields();

      const groups = await fetchPreviewGroups();
      setPreviewGroups(groups);
    } catch (error) {
      console.error('Preview failed:', error);
      notification.error({
        message: t('clientInform.previewError'),
        description: error?.response?.data?.message || t('clientInform.previewFailed'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSend = async () => {
    try {
      setIsSubmitting(true);
      await form.validateFields();

      const groups = buildPayloadGroups();
      const results = await Promise.allSettled(
        groups.map(group => clientInformApi.send(group.payload))
      );

      const succeeded = [];
      const failed = [];

      results.forEach((result, index) => {
        const group = groups[index];
        if (result.status === 'fulfilled' && result.value.data?.success) {
          succeeded.push({ group, message: result.value.data.message || t('clientInform.emailsSentSuccess') });
        } else {
          const message = result.status === 'fulfilled'
            ? result.value.data?.message
            : result.reason?.response?.data?.message;
          failed.push({ group, message: message || t('clientInform.emailsSendFailed') });
        }
      });

      if (failed.length === 0) {
        notification.success({
          message: t('clientInform.sendSuccess'),
          description: (
            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
              {succeeded.map(({ group, message }, idx) => (
                <li key={idx}>{group.label}: {message}</li>
              ))}
            </ul>
          ),
        });
        onClose();
      } else {
        notification.warning({
          message: succeeded.length > 0 ? t('clientInform.sendPartialSuccess') : t('clientInform.sendError'),
          description: (
            <div>
              {succeeded.length > 0 && (
                <>
                  <p style={{ marginBottom: 4 }}>{t('clientInform.sendSuccess')}:</p>
                  <ul style={{ marginTop: 0, marginBottom: 8, paddingLeft: 20 }}>
                    {succeeded.map(({ group, message }, idx) => (
                      <li key={idx}>{group.label}: {message}</li>
                    ))}
                  </ul>
                </>
              )}
              <p style={{ marginBottom: 4 }}>{t('clientInform.sendError')}:</p>
              <ul style={{ marginTop: 0, paddingLeft: 20 }}>
                {failed.map(({ group, message }, idx) => (
                  <li key={idx}>{group.label}: {message}</li>
                ))}
              </ul>
            </div>
          ),
          duration: 0,
        });
      }
    } catch (error) {
      console.error('Send failed:', error);
      notification.error({
        message: t('clientInform.sendError'),
        description: error?.response?.data?.message || t('clientInform.emailsSendFailed'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderContactsCell = useCallback((unit) => (
    <RecipientsCell
      contacts={contactsByUnitId.get(getBuildingUnitId(unit))}
      loading={loadingContacts}
    />
  ), [contactsByUnitId, loadingContacts]);

  const hasUnitMissingData = useCallback((unit) => {
    const hasPartner = unit.partner_id || unit.partner?.id;
    return !hasPartner;
  }, []);

  const getPartnerName = useCallback((unit) => {
    const partner = unit.partner || unit.service_rrules?.at(-1)?.partner;
    if (!partner) return '-';
    
    return `${partner.first_name || ''} ${partner.last_name || ''}`.trim() || '-';
  }, []);

  const columns = useMemo(() => [
    {
      title: t('clientInform.address'),
      dataIndex: 'unit',
      key: 'address',
      width: 200,
      render: (unit) => {
        const street = unit?.building_unit?.street || '';
        const houseNumber = unit?.building_unit?.house_number || '';
        const fullAddress = `${street} ${houseNumber}`.trim() || '-';
        const isMissing = hasUnitMissingData(unit);
        
        return (
          <Input 
            disabled 
            value={fullAddress}
            style={isMissing ? { borderColor: '#ff4d4f', backgroundColor: '#fff2f0' } : {}}
          />
        );
      },
    },
    {
      title: t('clientInform.partnerForCleaning'),
      dataIndex: 'unit',
      key: 'partner',
      width: 150,
      render: (unit) => {
        const isMissing = hasUnitMissingData(unit);
        return (
          <Input 
            disabled 
            value={getPartnerName(unit)}
            style={isMissing ? { borderColor: '#ff4d4f', backgroundColor: '#fff2f0' } : {}}
          />
        );
      },
    },
    {
      title: t('clientInform.emailRecipients'),
      dataIndex: 'unit',
      key: 'contacts',
      width: 200,
      render: renderContactsCell,
    },
    {
      title: t('clientInform.compensationDate'),
      dataIndex: 'unit',
      key: 'compensation_date',
      width: 200,
      render: (unit) => (
        <Form.Item
          name={['compensation_date', unit.id]}
          rules={[{
            validator: (_, value) => (value?.some(Boolean)
              ? Promise.resolve()
              : Promise.reject(new Error(t('clientInform.selectCompensationDate')))),
          }]}
          style={{ marginBottom: 0 }}
        >
          <MultiDatePicker />
        </Form.Item>
      ),
    },
    {
      title: t('clientInform.changeWeek'),
      dataIndex: 'unit',
      key: 'change_week',
      width: 250,
      render: (unit) => {
        const initialValue = unit.action_week_from && unit.action_week_to
          ? [moment(unit.action_week_from), moment(unit.action_week_to)]
          : null;
        
        return (
          <Form.Item
            name={['change_week', unit.id]}
            initialValue={initialValue}
            rules={[{ required: true, message: t('clientInform.selectChangeWeek') }]}
            style={{ marginBottom: 0 }}
          >
            <RangePicker style={{ width: '100%' }} />
          </Form.Item>
        );
      },
    },
  ], [renderContactsCell, hasUnitMissingData, getPartnerName, t]);

  return (
    <Modal
      title={t('clientInform.confirmationCleaningDayRescheduling')}
      open={visible}
      onCancel={onClose}
      width={1400}
      footer={null}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <div style={{ display: showConfirmation ? 'none' : 'block' }}>
          {/* Header section with bulk controls */}
          <Row gutter={16}>
              <Col span={4}>
                <Form.Item label={t('clientInform.managerSignature')} name="manager_employee_id">
                  <ManagerSelect
                    withFormItem={false}
                    clearable
                    showSearch
                    activeOnly
                    placeholder={t('clientInform.selectManager')}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label={t('clientInform.bulkTimeRangeWeek')}>
                  <RangePicker
                    style={{ width: '100%' }}
                    onChange={applyBulkTimeRange}
                    placeholder={[t('clientInform.from'), t('clientInform.to')]}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label={t('clientInform.bulkCompensationDate')}>
                  <MultiDatePicker
                    value={bulkCompensationDates}
                    onChange={(dates) => {
                      setBulkCompensationDates(dates);
                      applyBulkCompensationDate(dates);
                    }}
                    placeholder={t('clientInform.selectDate')}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            {loadingContacts && (
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <Spin indicator={<LoadingOutlined spin />} />
                <span style={{ marginLeft: 8, color: '#999' }}>
                  {t('clientInform.loadingContacts')}
                </span>
              </div>
            )}
            <Table
              columns={columns}
              dataSource={tableData}
              pagination={false}
              scroll={{ x: 1200 }}
              size="small"
            />

            <Row justify="end" style={{ marginTop: 16 }}>
              <Button
                type="primary"
                icon={<EyeOutlined />}
                onClick={handleValidateAndPreview}
                loading={isSubmitting}
              >
                {t('clientInform.emailPreview')}
              </Button>
            </Row>
          </div>

          <div style={{ display: showConfirmation ? 'block' : 'none' }}>
            <Row justify="space-between" style={{ marginBottom: 16 }}>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={goBackToForm}
                disabled={isSubmitting}
              >
                {t('clientInform.backToForm')}
              </Button>
              <div>
                <Button
                  icon={<EyeOutlined />}
                  onClick={handlePreview}
                  disabled={isSubmitting}
                  style={{ marginRight: 8 }}
                >
                  {t('clientInform.updatePreview')}
                </Button>
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={handleSend}
                  loading={isSubmitting}
                >
                  {t('clientInform.confirmAndSend')}
                </Button>
              </div>
            </Row>

            {previewGroups.length > 1 && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message={t('clientInform.groupsDetectedTitle', { count: previewGroups.length })}
                description={
                  <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                    {previewGroups.map((group, idx) => (
                      <li key={idx}>{group.label}</li>
                    ))}
                  </ul>
                }
              />
            )}

            {previewGroups.map((group, idx) => (
              <Card
                key={group.key || idx}
                title={t('clientInform.groupPreviewLabel', {
                  index: idx + 1,
                  count: previewGroups.length,
                  label: group.label,
                })}
                style={{ marginBottom: 16 }}
              >
                <RecipientsSummary
                  recipients={group.recipients}
                  buildingsWithoutContacts={group.buildingsWithoutContacts}
                />
                <iframe
                  title={`Email Preview ${idx + 1}`}
                  style={{ width: '100%', height: '500px', border: '1px solid #ccc' }}
                  srcDoc={group.html}
                />
              </Card>
            ))}
          </div>
      </Form>
    </Modal>
  );
};

export default CleaningDayReschedulingModal;
